import { TEMPLATE_STRINGS, renderTemplate } from '../utils/templateLoaderUtil.js';
import { getCompleteIntroContent } from './introParagraphService.js';

// Add a new function to get dynamic signature from Graph
export async function getEnhancedSignature(userInfo, accessToken = null) {
  let signatureData = {
    name: userInfo.name,
    email: userInfo.email,
    title: 'VAS - Employee',
    company: 'CBRE | Valuation & Advisory Services',
    address: '1234 Maple ST. | New York, NY 10075',
    phone: '123-234-3456',
    website: 'www.cbre.com'
  };

  // If we have Graph access, enhance with real data
  if (accessToken) {
    try {
      const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (userResponse.ok) {
        const graphUser = await userResponse.json();
        console.log('Graph user data:', graphUser);
        // Override defaults with Graph data when available
        signatureData = {
          name: graphUser.displayName || userInfo.name,
          email: graphUser.mail || graphUser.userPrincipalName || userInfo.email,
          title: graphUser.jobTitle || 'VAS - Employee',
          company: 'CBRE | Valuation & Advisory Services',
          address: graphUser.officeLocation || '1234 Maple ST. | New York, NY 10075',
          phone: graphUser.businessPhones?.[0] || graphUser.mobilePhone || '123-234-3456',
          website: 'www.cbre.com'
        };
      }
    } catch (error) {
      console.warn('Could not fetch Graph user data, using defaults:', error);
    }
  }

  return signatureData;
}

// Data request mappings function (keeping this as is)
function getAllDataRequestMappings() {
    return {
      'Auto Dealership Existing': [
        'Three-year and YTD income and expenses',
        'Current year income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Breakdown of building area by office, showroom, service, parts and mezzanine areas',
        'Historical sales volumes for both new and used cars',
        'Revenue breakdown by department (new cars, used cars, parts and service)',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Auto Dealership Proposed': [
        'Pro forma revenue breakdown by department (new cars, used cars, parts and service)',
        'Pro forma sales volumes for both new and used cars',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Breakdown of building area by office, showroom, service, parts and mezzanine areas',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Golf - Private': [
        'Three-year and YTD property detailed income and expenses (departmentalized)',
        'Round counts past 3 years and current budget/YTD',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Copy of club bylaws and/or membership contract',
        'List of membership categories and breakdown of club membership by category',
        'Summary of membership levels by category for the last three years',
        'Details on various membership fees and dues by category of membership',
        'Name and telephone number of individual responsible for membership to discuss average rollover, absorption, etc',
        'Golf course designer? Year opened?',
        'Maintenance equipment breakdown with cost; or if leased, details on lease payments',
        'Golf cart cost, age, number of carts; and if leased, payment details and length of lease',
        'Golf course acreage maintained, irrigation water source and cost, irrigation system type and design, grasses on tees, fairways, rough, and greens',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Golf - Semi Private': [
        'Three-year and YTD or current year budget property income and expenses (departmentalized)',
        '18-hole equivalent round counts past 3 calendar years and current YTD',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Daily fee rates for peak season, shoulder season, and off-season',
        'Number of members and membership rates, if applicable',
        'Golf course designer? Year opened? Renovated?',
        'Maintenance equipment breakdown with cost; or if leased, details on payments',
        'Golf cart cost, age, number of carts; and if leased, payment details and length of lease',
        'Golf course description including: a. Acreage maintained, b. Water source and cost, c. Greens construction, d. Irrigation system type and design, e. Grasses on tees, fairways, rough, and greens',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Healthcare Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Marketing plan and/or local competitive study, if available',
        'Detailed occupancy report for the past three years and current YTD',
        'Detailed current rent roll indicating any vacant units',
        'Details regarding any pending changes to the rent roll, including deposits for future occupancies',
        'Details regarding any concessions currently being offered or provided for all leases pending or signed over the prior 12 months',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Analysis of prospective payment system (Medicaid, Medicare, and private pay)',
        'Details regarding the historical and future replacement schedule (i.e., carpets, appliances, cabinetry, laundry facilities, HVAC, etc.)',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Healthcare Proposed': [
        'Pro forma income and expense projections',
        'Budgeted occupancy report',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Analysis of prospective payment system (Medicaid, Medicare, and private pay)',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Hotel/Motel Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Smith Travel Research STR report (if your property participates)',
        'Detailed occupancy report for the past three years and current YTD',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Franchise and/or management agreement, including fee calculation',
        'Annual ADR and average occupancy rates for past three years and current YTD',
        'Details regarding the historical and future replacement schedule (i.e., carpets, FF&E, laundry facilities, HVAC, etc.)',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and rooms',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Hotel/Motel Proposed': [
        'Pro forma income and expense projections',
        'Pro forma forecast ADR and overall occupancy rate for the first two years of operation',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Franchise and/or management agreement, including fee calculation',
        'Detailed cost breakdown of pre-opening expenses and FF&E',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and rooms',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Indust./ST Fee Simple Existing': [
        'Three-year and YTD expenses',
        'Current year expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Details regarding existing percentage of office build-out and climate controlled area',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Indust./ST Fee Simple Proposed': [
        'Pro forma income and expense projections',
        'Details regarding existing percentage of office build-out and climate controlled area',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Indust./ST Fee Leased Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Details regarding existing percentage of office build-out and climate controlled area',
        'Lease agreement',
        'Details regarding any pending changes or pertinent information regarding the current/future status of the tenant',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Details regarding any tenant improvement allowances and free rent provided',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/MT Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Detailed occupancy report for the past three years and current YTD',
        'Expense reimbursement schedule on a tenant-by-tenant basis, including base-year amounts',
        'Complete copies or abstracts of all lease agreements and a current rent roll',
        'Details regarding the lease rates/terms and marketing activity for any vacant suites',
        'Details regarding any pending changes to the rent roll or pertinent information regarding the current/future status of the tenants',
        'Details regarding any tenant improvement allowances and free rent provided for all leases pending or signed over the prior 12 months',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and suites',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Land Vacant': [
        'Planning/Zoning application or approval, if applicable',
        'Any previous market/demand studies or appraisals',
        'Site plan for proposed or entitled development, if applicable',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Indust./ST Fee Leased Proposed': [
        'Pro forma income and expense projection',
        'Details regarding existing percentage of office build-out and climate controlled area',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Complete pending lease agreement',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Industrial/MT Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Current rent roll',
        'Details regarding existing percentage of office build-out and climate controlled area',
        'Lease agreements and abstracts',
        'Details regarding any pending changes or pertinent information regarding the current/future status of the tenants',
        'Details regarding any tenant improvement allowances and free rent provided',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Industrial/MT Proposed': [
        'Pro forma income and expense projections',
        'Pro forma percentage of office build-out and climate controlled area',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Details regarding any tenant improvement allowances and free rent offered',
        'Details regarding the proposed lease rates/terms and marketing activity to date',
        'Complete copies of all Letter of intent and pending lease agreements',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and suites',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/MT Proposed': [
        'Pro forma income and expense projections',
        'Details regarding any tenant improvement allowances and free rent offered',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Details regarding the proposed lease rates/terms and marketing activity to date',
        'Complete copies of all Letter of intent and pending lease agreements',
        'Pro forma percentage of office build-out and climate controlled area',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and suites',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/ST Fee Simple Existing': [
        'Three-year and YTD expenses',
        'Current year expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/ST Fee Simple Proposed': [
        'Details regarding projected property expenses',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/ST Fee Leased Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Lease agreement',
        'Details regarding any pending changes or pertinent information regarding the current/future status of the tenant',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Details regarding any tenant improvement allowances and free rent provided',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Office/ST Fee Leased Proposed': [
        'Pro forma income and expense projections',
        'Complete pending lease agreement',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Details regarding any tenant improvements allowance and free rent',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Reside. Mobile Home Park Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Marketing plan and/or local competitive study, if available',
        'Detailed occupancy report for the past three years and current YTD',
        'Detailed current rent roll indicating any vacant pads',
        'Details regarding any pending changes to the rent roll, including deposits for future occupancies',
        'Details regarding any concessions currently being offered or provided for all leases pending or signed over the prior 12 months',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings on site',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Reside. Mobile Home Park Proposed': [
        'Pro forma income and expense projections',
        'Details regarding any planned rental concessions to be included in the initial lease-up of the project',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Budgeted occupancy report',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Marketing plan, absorption projections and/or local competitive study',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Residential Multifamily Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Detailed occupancy report for the past three years and current YTD',
        'Detailed current rent roll indicating any vacant units',
        'Details regarding any pending changes to the rent roll, including deposits for future occupancies',
        'Details regarding any concessions currently being offered or provided for all leases pending or signed over the prior 12 months',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Details regarding the historical and future replacement schedule (i.e., carpets, appliances, cabinetry, laundry facilities, HVAC, etc.)',
        'Marketing plan and/or local competitive study, if available',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Residential Multifamily Proposed': [
        'Pro forma income and expense projections',
        'Details regarding any planned rental concessions to be included in the initial lease-up of the project',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Budgeted occupancy report',
        'Details regarding all personal property, including furniture, fixtures, and equipment',
        'Marketing plan, absorption projections and/or local competitive study',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Residential Subdivision Existing': [
        'Any marketing reports and/or local competitive studies',
        'Lot-by-lot summary of gross lot and net pad areas',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Individual home plans, elevations and square footages',
        'Detailed absorption report, if applicable',
        'Details regarding any sales concessions offered',
        'Details regarding any lot premiums',
        'Details on any sale, contract, or listing of the overall property within the past three years',
        'Homeowner\'s association agreement and details on membership costs, if applicable',
        'Current county property tax assessment or tax bill and information on the projected effective tax rate (including all potential special assessments)',
        'Site plan for development',
        'Building plans and specifications, including square footage, for any community facilities',
        'Details on any sale, contract, or listing of any individual lots/homes within the past 12 months',
        'Survey and/or plat map',
        'Current title report and title holder name',
        'Legal description',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Residential Subdivision Proposed': [
        'Current county property tax assessment or tax bill and information on the projected effective tax rate (including all potential special assessments)',
        'Lot-by-lot summary of gross lot and net pad areas',
        'Development pro forma (including product pricing/sales summary, hard costs, soft costs and absorption estimate)',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Individual home plans, elevations and square footages',
        'Homeowner\'s association agreement and details on membership costs, if applicable',
        'Details regarding any proposed sales concessions to be offered',
        'Details regarding any proposed lot premiums',
        'Any marketing reports and/or local competitive studies',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage, for any community facilities',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Current title report and title holder name',
        'Legal description',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/MT Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Detailed occupancy report for the past three years and current YTD',
        'Expense reimbursement schedule on a tenant-by-tenant basis',
        'Historical sales volumes for all tenants subject to percentage rent',
        'Complete copies or abstracts of all lease agreements and a current rent roll',
        'Details regarding any pending changes to the rent roll or pertinent information regarding the current/future status of the tenants',
        'Details regarding the lease rates/terms and marketing activity for any vacant suites',
        'Details regarding any tenant improvement allowances and free rent provided for all leases pending or signed over the prior 12 months',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and suites',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/MT Proposed': [
        'Pro forma income and expense projections',
        'Details regarding the proposed lease rates/terms and marketing activity to date',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Complete copies of all Letter of intent and pending lease agreements',
        'Details regarding any tenant improvement allowances and free rent offered',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and suites',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/ST Fee Simple Existing': [
        'Three-year and YTD expenses',
        'Current year expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Historical sales volumes, if applicable',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/ST Fee Simple Proposed': [
        'Details regarding projected property expenses',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/ST Leased Fee Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Historical sales volumes, if applicable',
        'Lease agreement',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Details regarding any pending changes or pertinent information regarding the current/future status of the tenant',
        'Details regarding any tenant improvement allowances and free rent provided',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Retail/ST Leased Fee Proposed': [
        'Pro forma income and expense projections',
        'Complete pending lease agreement',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Tenant\'s most recent Annual Report and 10K Report',
        'Details regarding any tenant improvements allowance and free rent',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Self-Storage Existing': [
        'Three-year and YTD property income and expenses',
        'Current year property income and expense budget',
        'Details regarding the development costs, including land cost, if developed within the past three years',
        'Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months',
        'Any previous market/demand studies or appraisals',
        'Detailed occupancy report for the past three years and current YTD',
        'Current Unit Mix Summary report',
        'Current rent schedule',
        'Detailed current rent roll indicating any vacant units',
        'Details regarding any pending changes to the rent roll, including deposits for future occupancies',
        'Details regarding any concessions currently being offered or provided for all leases pending or signed over the prior 12 months',
        'Marketing plan and/or local competitive study, if available',
        'Site plan for the existing development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ],
      'Self-Storage Proposed': [
        'Pro forma income and expense projections',
        'Proposed Unit Mix Summary report',
        'Details regarding the development costs, including land costs',
        'Details regarding construction timeline',
        'Any previous market/demand studies or appraisals',
        'Budgeted occupancy report',
        'Pro forma rent schedule',
        'Details regarding any planned rental concessions to be included in the initial lease-up of the project',
        'Marketing plan, absorption projections and/or local competitive study',
        'Site plan for proposed or entitled development',
        'Building plans and specifications, including square footage for all buildings and units',
        'Details on any sale, contract, or listing of the property within the past three years',
        'Survey and/or plat map',
        'Ground lease, if applicable',
        'Current title report and title holder name',
        'Legal description',
        'Current county property tax assessment or tax bill',
        'Engineering studies, soil tests or environmental assessments',
        'Green Certifications and/or Energy Performance Ratings',
        'Source of Fuel and Renewable Energy, if applicable',
        'Energy Audits, if completed',
        'Any other information that might be helpful in valuing this property'
      ]
    };
  }

// Function to get data request items for a specific property type
function getDataRequestItems(dataRequestType) {
  const mappings = getAllDataRequestMappings();
  return mappings[dataRequestType] || [];
}

// Function to generate dynamic list items HTML
function generateDataRequestList(dataRequestType) {
  const items = getDataRequestItems(dataRequestType);
  
  if (items.length === 0) {
    // Fallback to default list if no mapping found
    return `
      <li>Three-year and YTD property income and expenses</li>
      <li>Current year property income and expense budget</li>
      <li>Details regarding the development costs, including land cost, if developed within the past 10 years</li>
      <li>Details regarding capital expenditures made within the last 12 months, or scheduled for the next 12 months</li>
      <li>Any previous market/demand studies or appraisals</li>
      <li>Lease agreement</li>
      <li>Details regarding any pending changes or pertinent information regarding the current/ future status of the tenant</li>
      <li>Current Real Estate Tax Bill and/or most recent 10K Report</li>
      <li>Details regarding any tenant improvement allowances and free rent provided</li>
      <li>Site plan for the existing development</li>
      <li>Building plans and specifications, including square footage</li>
      <li>Details on any sale, contract, or listing of the property within the past three years</li>
      <li>Survey and/or plat map</li>
      <li>Ground lease, if applicable</li>
      <li>Current title report and title holder name</li>
      <li>Legal description</li>
      <li>Current county property tax assessment or tax bill</li>
      <li>Engineering studies, soil tests or environmental assessments</li>
      <li>Green Certifications and/or Energy Performance Ratings</li>
      <li>Source of Fuel and Renewable Energy, if applicable</li>
      <li>Energy Audits, if completed</li>
      <li>Any other information that might be helpful in valuing this property</li>
    `;
  }
  
  // Generate HTML list items from the mapping
  return items.map(item => `<li>${item}</li>`).join('\n                      ');
}

const toPascalCase = (str) => {
    if (!str) return str;
    return str.replace(/\w+/g, (word) => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
  };
// NEW: Template rendering functions
function renderAppraiserIntroductionTemplate(userInfo, timestamp, data, signatureData) {
  const sig = signatureData || {
    name: userInfo.name,
    email: userInfo.email,
    title: 'VAS - Employee',
    company: 'CBRE | Valuation & Advisory Services',
    address: '1234 Maple ST. | New York, NY 10075',
    phone: '123-234-3456',
    website: 'www.cbre.com'
  };

  // Build dynamic data request list (<li> items)
  let dataRequestList = '';
  
  // Check if this is a custom data request with content
  if (data.selectedDataRequest && data.selectedDataRequest.content) {
    // Use the custom content as-is without wrapping in <li> tags
    // Clean and format the HTML content similar to intro paragraph
    let customContent = data.selectedDataRequest.content;
    
    // If it's plain text, just use it as-is
    if (!customContent.includes('<') && !customContent.includes('>')) {
      dataRequestList = customContent.replace(/\n/g, '<br>');
    } else {
      // Clean up the HTML while preserving all formatting
      dataRequestList = customContent
        // Handle div transitions
        .replace(/<\/div>\s*<div[^>]*>/gi, '</div><br><div>')
        .replace(/<div[^>]*>/gi, '')
        .replace(/<\/div>/gi, '')
        // Handle p tags
        .replace(/<\/p>\s*<p[^>]*>/gi, '</p><br><p>')
        .replace(/<p[^>]*>/gi, '')
        .replace(/<\/p>/gi, '')
        // Convert newlines to br
        .replace(/\n/g, '<br>')
        // Clean up multiple consecutive br tags
        .replace(/(<br>\s*){2,}/gi, '<br>')
        // Remove trailing br
        .replace(/<br>\s*$/, '')
        // Clean up whitespace
        .replace(/\s+/g, ' ')
        .trim();
    }
  } else {
    // Use system-defined data request list
    dataRequestList = generateDataRequestList(data.dataRequest);
  }

  // Sender & primary valuer
  const senderName = userInfo.name || 'Sender Name';

  // Property county (optional)
  let propertyCounty = data.propertyCounty || '';
  // Try naive extraction if not provided and address contains last token 'County'
  if (!propertyCounty && typeof data.propertyAddress === 'string') {
    const match = data.propertyAddress.match(/([A-Za-z\s]+County)\b/);
    if (match) propertyCounty = match[1];
  }

  // Get intro paragraph content
  let introParagraphContent = '';
  let useDefaultIntro = true;
  let introContent = '';
  
  if (data.introParagraphId && data.introParagraphId !== 'default' && data.selectedIntroParagraph && data.selectedIntroParagraph.content) {
    // Use the selected custom intro paragraph (not default)
    useDefaultIntro = false;
    let rawIntroContent = renderTemplate(data.selectedIntroParagraph.content, {
      senderName: senderName,
      primaryValuer: data.primaryValuer,
      clientName: data.clientName,
      sigEmail: data.sigEmail ? data.sigEmail.toLowerCase() : ''
    });
    
    // Format intro content using the exact same approach as the signature preserveRichTextFormatting
    // If it's plain text, just add br tags
    if (!rawIntroContent.includes('<') && !rawIntroContent.includes('>')) {
      introContent = rawIntroContent.replace(/\n/g, '<br>');
    } else {
      // Clean up the HTML while preserving all formatting tags (same as signature processing)
      introContent = rawIntroContent
        // First, handle div to div transitions with br tags
        .replace(/<\/div>\s*<div[^>]*>/gi, '</div><br><div>')
        // Then remove all div tags but keep the br tags we just added
        .replace(/<div[^>]*>/gi, '')
        .replace(/<\/div>/gi, '')
        // Handle p tags the same way
        .replace(/<\/p>\s*<p[^>]*>/gi, '</p><br><p>')
        .replace(/<p[^>]*>/gi, '')
        .replace(/<\/p>/gi, '')
        // Convert any remaining newlines to br tags
        .replace(/\n/g, '<br>')
        // Clean up multiple consecutive br tags
        .replace(/(<br>\s*){2,}/gi, '<br>')
        // Remove trailing br
        .replace(/<br>\s*$/, '')
      // Clean up whitespace
        .replace(/\s+/g, ' ')
        .trim();
    }
  } else {
    // Use default intro (standard CBRE template content)
    useDefaultIntro = true;
  }

  const templateData = {
    clientName: data.clientName || '[CLIENT NAME]',
    fileId: data.fileId || '[FILE ID]',
    propertyAddress: data.propertyAddress 
      ? data.propertyAddress
          .replace(/United States/g, '')  // Remove "United States"
          .replace(/[\r\n]+/g, ', ')      // Replace line breaks with comma+space
          .replace(/\s*,\s*/g, ', ')      // Normalize comma spacing
          .replace(/,\s*,/g, ',')         // Remove duplicate commas
          .replace(/\s+/g, ' ')           // Normalize multiple spaces
          .trim()                         // Remove leading/trailing whitespace
          .replace(/,$/, '')              // Remove trailing comma
      : '[PROPERTY ADDRESS]',
    propertyCounty, // used in conditional block
    contactName: toPascalCase(data.contactName) || '(PROPERTY CONTACT NAME)',
    companyName: data.companyName,
    senderName: data.senderName || 'Sender',
    primaryValuer: data.primaryValuer || ('PRIMARY VALUER'),
    primaryValuerEmail: data.primaryValuerEmail,
    proposedInspectionDate: data.proposedInspectionDate || '(DATE)',
    proposedInspectionTime: data.proposedInspectionTime || '(TIME)',
    hasInspectionDetails: !!(data.proposedInspectionDate && data.proposedInspectionDate.trim()),
    dataRequestList,
    isCustomDataRequest: !!(data.selectedDataRequest && data.selectedDataRequest.content),
    useDefaultIntro,
    introContent,
    boxUploadUrl: data.boxUploadUrl,
    // Signature email reference in paragraph (sigEmail)
    sigEmail: data.sigEmail ? data.sigEmail.toLowerCase() : '',
    // Legacy / additional signature fields (in case other templates need them)
    sigName: sig.name,
    sigTitle: sig.title,
    sigCompany: sig.company,
    sigAddress: sig.address,
    sigPhone: sig.phone,
    sigWebsite: sig.website
  };

  return renderTemplate(TEMPLATE_STRINGS['Property Contact Request'], templateData);
}

function renderBugReportTemplate(userInfo, timestamp, data) {
  const templateData = {
    userName: userInfo.name,
    userEmail: userInfo.email,
    timestamp,
    assignmentId: data.assignmentId,
    workFile: data.workFile,
    currentAction: data.currentAction
  };

  return renderTemplate(TEMPLATE_STRINGS['bug-report'], templateData);
}

function renderSupportRequestTemplate(userInfo, timestamp, data) {
  const templateData = {
    userName: userInfo.name,
    userEmail: userInfo.email,
    timestamp,
    assignmentId: data.assignmentId,
    workFile: data.workFile
  };

  return renderTemplate(TEMPLATE_STRINGS['support-request'], templateData);
}

function renderDefaultTemplate(userInfo, timestamp, data) {
  const templateData = {
    userName: userInfo.name,
    userEmail: userInfo.email,
    timestamp
  };

  return renderTemplate(TEMPLATE_STRINGS['default'], templateData);
}

// Updated EMAIL_TEMPLATES object using the new template system
export const EMAIL_TEMPLATES = {
  'Property Contact Request': {
    subject: (data) => {
      console.log("Generating Email Subject::", data);
      
      const fullAddress = data.propertyAddress 
        ? data.propertyAddress
            .replace(/United States/g, '')  // Remove "United States"
            .replace(/[\r\n]+/g, ', ')      // Replace line breaks with comma+space
            .replace(/\s*,\s*/g, ', ')      // Normalize comma spacing
            .replace(/,\s*,/g, ',')         // Remove duplicate commas
            .replace(/\s+/g, ' ')           // Normalize multiple spaces
            .trim()                         // Remove leading/trailing whitespace
            .replace(/,$/, '')              // Remove trailing comma
        : '';
      
      // If property name exists, show "Appraisal of [propertyName] | [fullAddress]"
      // If property name doesn't exist, show "Appraisal of [fullAddress]" (no pipe, no redundant street address)
      if (data.propertyName && data.propertyName.trim()) {
        const propertyName = data.propertyName.replace(/[\r\n]/g, ' ').trim();
        return `Appraisal of ${propertyName} | ${fullAddress}`;
      } else {
        return `Appraisal of ${fullAddress}`;
      }
    },
    template: renderAppraiserIntroductionTemplate
  },
  'Client Introduction': {
    subject: (data) => {
      console.log("Generating Client Introduction Subject::", data);
      
      const fullAddress = data.propertyAddress 
        ? data.propertyAddress
            .replace(/United States/g, '')
            .replace(/[\r\n]+/g, ', ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/,\s*,/g, ',')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/,$/, '')
        : '';
      
      if (data.propertyName && data.propertyName.trim()) {
        const propertyName = data.propertyName.replace(/[\r\n]/g, ' ').trim();
        return `Client Contact Introduction ${propertyName} | ${fullAddress}`;
      } else {
        return `Client Contact Introduction | ${fullAddress}`;
      }
    },
    template: (userInfo, timestamp, data, signatureData) => {
      const templateString = TEMPLATE_STRINGS['Client Introduction'];
      
      const templateData = {
        clientContactFirstName: data.clientContactFirstName || 'Client',
        propertyAddress: data.propertyAddress || 'the property',
        primaryValuerName: data.primaryValuerName || 'the primary valuer',
        fileId: data.fileId || '',
        clientReference: data.clientReference || '',
        useDefaultIntro: data.useDefaultIntro !== false,
        introContent: data.introContent || '',
        isNewEmail: data.isNewEmail,
        signature: data.signature || ''
      };
      
      return renderTemplate(templateString, templateData);
    }
  },
  'Client Introduction PV': {
    subject: (data) => {
      const fullAddress = data.propertyAddress
        ? data.propertyAddress
            .replace(/United States/g, '')
            .replace(/[\r\n]+/g, ', ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/,\s*,/g, ',')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/,$/, '')
        : '';

      if (data.propertyName && data.propertyName.trim()) {
        const propertyName = data.propertyName.replace(/[\r\n]/g, ' ').trim();
        return `Client Contact Introduction ${propertyName} | ${fullAddress}`;
      } else {
        return `Client Contact Introduction | ${fullAddress}`;
      }
    },
    template: (userInfo, timestamp, data) => {
      const templateString = TEMPLATE_STRINGS['Client Introduction PV'];

      const templateData = {
        clientContactFirstName: data.clientContactFirstName || 'Client',
        originatorFullName: data.originatorFullName || 'the Job Originator',
        propertyAddress: data.propertyAddress || 'the property',
        fileId: data.fileId || '',
        clientReference: data.clientReference || '',
        useDefaultIntro: data.useDefaultIntro !== false,
        introContent: data.introContent || '',
        isNewEmail: data.isNewEmail,
      };

      return renderTemplate(templateString, templateData);
    }
  },
  'Client Introduction PV and JO': {
    subject: (data) => {
      const fullAddress = data.propertyAddress
        ? data.propertyAddress
            .replace(/United States/g, '')
            .replace(/[\r\n]+/g, ', ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/,\s*,/g, ',')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/,$/, '')
        : '';
      if (data.propertyName && data.propertyName.trim()) {
        const propertyName = data.propertyName.replace(/[\r\n]/g, ' ').trim();
        return `Client Contact Introduction ${propertyName} | ${fullAddress}`;
      } else {
        return `Client Contact Introduction | ${fullAddress}`;
      }
    },
    template: (userInfo, timestamp, data) => {
      const templateString = TEMPLATE_STRINGS['Client Introduction PV and JO'];
      const templateData = {
        clientContactFirstName: data.clientContactFirstName || 'Client',
        originatorFullName: data.originatorFullName || 'the Job Originator',
        propertyAddress: data.propertyAddress || 'the property',
        fileId: data.fileId || '',
        clientReference: data.clientReference || '',
        useDefaultIntro: data.useDefaultIntro !== false,
        introContent: data.introContent || '',
        isNewEmail: data.isNewEmail,
      };
      return renderTemplate(templateString, templateData);
    }
  },
    'Client Follow Up': {
    subject: (data) => {
      console.log("Generating Client Follow Up Subject::", data);
      
      const fullAddress = data.propertyAddress 
        ? data.propertyAddress
            .replace(/United States/g, '')
            .replace(/[\r\n]+/g, ', ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/,\s*,/g, ',')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/,$/, '')
        : '';
      
      // Only include propertyName if it exists and is not "Property" fallback
      const propertyName = (data.propertyName && data.propertyName.trim() && data.propertyName !== 'Property') 
        ? data.propertyName.replace(/[\r\n]/g, ' ').trim()
        : null;
      
      if (propertyName) {
        return `Follow Up for ${propertyName} | ${fullAddress}`;
      } else {
        return `Follow Up for ${fullAddress}`;
      }
    },
    template: (userInfo, timestamp, data, signatureData) => {
      const templateString = TEMPLATE_STRINGS['Client Follow Up'];
      
      const templateData = {
        clientContactFirstName: data.clientContactFirstName || 'Client',
        propertyAddress: data.propertyAddress || 'the property',
        primaryValuerName: data.primaryValuerName || 'the primary valuer',
        fileId: data.fileId || '',
        clientReference: data.clientReference || '',
        useDefaultIntro: data.useDefaultIntro !== false,
        introContent: data.introContent || '',
        isNewEmail: data.isNewEmail,
        signature: data.signature || ''
      };
      
      return renderTemplate(templateString, templateData);
    }
  },
  'Property Follow Up': {
    subject: (data) => {
      const propertyName = (data.propertyName && data.propertyName.trim() && data.propertyName !== 'Property')
        ? data.propertyName.replace(/[\r\n]/g, ' ').trim()
        : null;
      const propertyAddress = data.propertyAddress
        ? data.propertyAddress.replace(/[\r\n]+/g, ', ').trim()
        : '';

      if (propertyName && propertyAddress) {
        return `Follow Up for ${propertyName} | ${propertyAddress}`;
      } else if (propertyName) {
        return `Follow Up for ${propertyName}`;
      } else {
        return `Follow Up for ${propertyAddress}`;
      }
    },
    template: (userInfo, timestamp, data) => {
      const templateString = TEMPLATE_STRINGS['Property Follow Up'];

      const templateData = {
        propertyContactFirstName: data.propertyContactFirstName || '',
        propertyAddress: data.propertyAddress || 'the property',
        propertyName: data.propertyName || '',
        fileId: data.fileId || '',
        useDefaultIntro: data.useDefaultIntro !== false,
        introContent: data.introContent || '',
        isNewEmail: data.isNewEmail,
        signature: data.signature || ''
      };

      return renderTemplate(templateString, templateData);
    }
  },
};
// Main generation function (unchanged)
export async function generateEmailTemplate(templateKey, userInfo, dynamicData = {}, accessToken = null) {
  const timestamp = new Date().toLocaleString();
  const template = EMAIL_TEMPLATES[templateKey] || EMAIL_TEMPLATES['default'];

  const dataWithTimestamp = { ...dynamicData, timestamp };

  let signatureData = null;
  if (accessToken && templateKey === 'Property Contact Request') {
    signatureData = await getEnhancedSignature(userInfo, accessToken);
  }

  return {
    subject: typeof template.subject === 'function'
      ? template.subject(dataWithTimestamp)
      : template.subject,
    htmlBody: template.template(userInfo, timestamp, dataWithTimestamp, signatureData)
  };
}